import { useCallback, useEffect, useState } from "react";
import { Bell, Trash2, Pencil, AlertTriangle, CheckCircle2, History } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { api, formatApiError } from "../lib/api";
import AlarmFormDialog from "../components/charts/AlarmFormDialog";
import AlarmEventsSheet from "../components/charts/AlarmEventsSheet";
import { useT } from "../lib/i18n";
import { toast } from "sonner";

export default function Alarms() {
  const t = useT();
  const [alarms, setAlarms] = useState([]);
  const [chartsById, setChartsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingAlarm, setEditingAlarm] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [eventsAlarm, setEventsAlarm] = useState(null);
  const [showEvents, setShowEvents] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/alarms");
      setAlarms(data || []);
      // fetch the unique charts referenced
      const ids = [...new Set((data || []).map((a) => a.chart_id))];
      const map = {};
      for (const cid of ids) {
        try {
          const { data: chart } = await api.get(`/charts/${cid}/data`);
          map[cid] = { rows: chart.rows?.length, alarms: chart.alarms };
        } catch (err) {
          // Chart may have been deleted or datasource is offline; show as unavailable.
          console.warn(`alarm chart ${cid} fetch failed:`, err?.message);
          map[cid] = null;
        }
      }
      setChartsById(map);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function remove(id) {
    if (!window.confirm("Delete alarm?")) return;
    try {
      await api.delete(`/alarms/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="space-y-6" data-testid="alarms-page">
      <div>
        <p className="kpi-label">{t("nav.alarms")}</p>
        <h1 className="text-2xl font-bold tracking-tight">{t("alarm.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("alarm.subtitle")}</p>
      </div>

      <Card className="card-soft">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("common.name")}</TableHead>
                <TableHead>{t("alarm.condition")}</TableHead>
                <TableHead>{t("alarm.interval")}</TableHead>
                <TableHead>{t("alarm.recipients")}</TableHead>
                <TableHead>{t("alarm.last_value")}</TableHead>
                <TableHead>{t("common.status")}</TableHead>
                <TableHead className="text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">{t("common.loading")}</TableCell></TableRow>
              ) : alarms.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-12">
                    <Bell className="h-5 w-5 mx-auto mb-2" />
                    {t("alarm.no_alarms")}
                  </TableCell>
                </TableRow>
              ) : (
                alarms.map((a) => (
                  <TableRow key={a.id} data-testid={`alarm-row-${a.id}`}>
                    <TableCell><span className="font-medium">{a.name}</span></TableCell>
                    <TableCell className="font-mono text-xs">
                      {a.condition === "gt" ? ">" : "<"} {a.threshold}
                    </TableCell>
                    <TableCell className="text-xs">{a.interval_minutes} min</TableCell>
                    <TableCell className="text-xs">
                      {(a.recipient_emails || []).length === 0 ? "—" : a.recipient_emails.join(", ")}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{a.last_value ?? "—"}</TableCell>
                    <TableCell>
                      {a.triggered ? (
                        <Badge className="bg-destructive/10 text-destructive border-destructive/30" variant="outline">
                          <AlertTriangle className="h-3 w-3 mr-1" /> {t("alarm.triggered")}
                        </Badge>
                      ) : a.enabled ? (
                        <Badge variant="outline" className="border-emerald-500/30 text-emerald-600 dark:text-emerald-400 bg-emerald-500/10">
                          <CheckCircle2 className="h-3 w-3 mr-1" /> {t("alarm.ok")}
                        </Badge>
                      ) : (
                        <Badge variant="outline">{t("alarm.paused")}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => {
                            setEventsAlarm(a);
                            setShowEvents(true);
                          }}
                          data-testid={`history-alarm-${a.id}`}
                          title="View history"
                        >
                          <History className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => {
                            setEditingAlarm(a);
                            setShowForm(true);
                          }}
                          data-testid={`edit-alarm-${a.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => remove(a.id)}
                          data-testid={`delete-alarm-${a.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AlarmFormDialog
        open={showForm}
        onOpenChange={setShowForm}
        alarm={editingAlarm}
        chart={editingAlarm ? { id: editingAlarm.chart_id, title: "Chart" } : null}
        onSaved={load}
      />
      <AlarmEventsSheet
        open={showEvents}
        onOpenChange={setShowEvents}
        alarm={eventsAlarm}
      />
    </div>
  );
}
